/**
 * The ITEMS stream's numbers (spec 02 §7.3-§7.5, §9.3, §9.4), each traced in the rebb64 disassembly.
 * Where the spec and the code differ the code's value is here and the routine is named.
 */

/** Stored score units are displayed points / 10 (02 §7). */
export const UNITS_PER_POINT = 10;

// --- The two timed slots (level-setup.s D_2BBD..D_2C8C, bubbles-sprites.s $1578, irq-handlers.s $06C6) ---

/** Foods run 0..46; the choice is capped here (L_2BB0: cmp #$2F / lda #$2E). */
export const FOOD_MAX = 46;
/** A new game's food base: 10 + rand(0..31) (game-loop.s $093F: rand & $1E, + $0A with the carry). */
export const NEW_GAME_BASE = 10;
export const NEW_GAME_SPAN = 32;
/** Food base + rand & 3 ($2BB3). */
export const FOOD_ROLL_MASK = 3;
/** The food's timer: 3 + rand & 7 seconds ($2C5B); it shows when the timer first goes below 0. */
export const FOOD_DELAY = 3;
export const FOOD_DELAY_MASK = 7;
/** The power-up's timer: 1 + rand & 15 seconds ($2C65). */
export const POWERUP_DELAY = 1;
export const POWERUP_DELAY_MASK = 15;
/** Each time a slot's timer goes below 0 it is set back to 11 ($1582): so an item is visible 12 s. */
export const SHOWN_TIMER = 11;
/** Frames in a second of the per-second timers (TXTTAB, $06B4: 50 frames). */
export const SECOND_FRAMES = 50;
/** The slots' VIC frame: tile x 8 + $14, tile y 8 + $2D (D_2C8C), as a bubble on the grid. */
export const SLOT_X = 0x14;
export const SLOT_Y = 0x2d;
/** A player takes an item within 16 px on both axes (D_2CDF..$2CFD). */
export const PICKUP_RANGE = 16;

// --- The power-up's choice (L_2BFC) and the doors ($2BDB) ---

/**
 * CIA 1 timer B's low byte, which D_E9EA XORs into every roll. The game never starts that timer; the
 * KERNAL's serial-bus timeout leaves its latch at $01FF, so it reads $FF - which is what makes geon's
 * reading of the designated power-up, (32 - 2 * levelIndex) & 31, come out of the LFSR.
 */
export const CIA_TIMER_B_LOW = 0xff;
/** rand & 15: 0 picks the crystal ball or bell ($1E | rand & 1), 7..15 the designated item, 1..6 any of 0..31. */
export const CHOICE_MASK = 15;
export const DESIGNATED_FROM = 7;
export const BALL_OR_BELL = 0x1e;
export const ANY_POWERUP_MASK = 31;
/** The door counter gains 10 per door ($2BE7); when it reaches 58 the door is white and it jumps to 78 ($2BEF). */
export const DOOR_STEP = 10;
export const WHITE_DOOR_AT = 58;
export const AFTER_WHITE_DOOR = 78;

// --- The carry: the next level's food base (bubbles-sprites.s $16F7, $1621) ---

/** After a clear: seconds left on the level timer + 8 ($1715). */
export const CLEAR_BASE_ADD = 8;
/** The Baron came ($1621: $58 = $FF): the next clear rolls rand & 3 instead ($170E). */
export const BARON_BASE = 0xff;
/** The bonus stage leaves the level timer at 50 ($3117), so its clear gives 58, capped to corn (46). */
export const BONUS_TIMER_AFTER = 50;

// --- Effects (special-item-effects.s D_2D65 / D_2D88 and the routines they name) ---

/** Red cross: $12 in D_A783, a bolt for each blow while it is >= 0 (L23B8): 19 bolts. */
export const RED_CROSS_BOLTS = 19;
/** Clock: SESSION = $87 main-loop passes ($2F74), counted down in D_1578. */
export const CLOCK_PASSES = 135;
/** Umbrellas: 3 or 7 levels on ($7FA4 / $7FA7), capped at level 100 (cmp #$63). */
export const SKIP_BLUE = 3;
export const SKIP_PURPLE = 7;
/** The white door's target: level index $45 ($7C37). */
export const WARP_TO = 70;
/** Umbrella and white door: the border flashes 6 x 10 frames before the levels scroll ($7FBC). */
export const SKIP_FLASH_FRAMES = 60;

/** Yellow cross: 2 + rand & 3 strikes (D_A653). */
export const STRIKES_MIN = 2;
export const STRIKES_MASK = 3;
/** A strike starts with the border flashing 4 x 5 frames, play paused (D_1AB6). */
export const STRIKE_FLASH_FRAMES = 20;
/** It starts at row 2 (the C64's bubble rows: tile row + 3), column $16 + rand & 7 (D_1AB6). */
export const STRIKE_ROW = 2;
export const STRIKE_COL = 0x16;
export const STRIKE_COL_MASK = 7;
/** Each pass one column left and one row down (L_19CF) until row $16. */
export const STRIKE_LAST_ROW = 0x16;
/** Its kill box: 48 x 48 from (col x 8 + $0C, row x 8 + $0D) (L_19CF). */
export const STRIKE_BOX = 0x30;
export const STRIKE_BOX_X = 0x0c;
export const STRIKE_BOX_Y = 0x0d;
/** Each step but the last: rand < 6 picks a monster slot 0..5 to kill (L_1A5F). */
export const STRIKE_RANDOM_KILL = 6;
/** After the last step its slots vanish ($38, $36: two passes) before the next strike (L_19C1). */
export const STRIKE_VANISH_PASSES = 2;

/** Blue cross: the water fills 25 rows from the bottom, one every 2 frames (L_2E15). */
export const FLOOD_ROWS = 25;
export const FLOOD_FRAMES_PER_ROW = 2;

/** Purple lamp and bomb: the border flashes 6 x 3 frames, play paused (D_2EEA). */
export const BLAST_FLASH_FRAMES = 18;
/** Purple lamp: six pieces from VIC ($88, $91) (D_2ECC), moved per I-tick by D_A7A3 / D_A7A8 (D_2A18). */
export const LAMP_X = 0x88;
export const LAMP_Y = 0x91;
export const LAMP_VX: readonly number[] = [0, 4, 4, 0, -4, -4];
export const LAMP_VY: readonly number[] = [-4, -2, 2, 4, 2, -2];
/** ... until the first piece is above Y $50 ($2A3B): 17 I-ticks. */
export const LAMP_END_Y = 0x50;
/**
 * Bomb (D_35B0, D_2A74): six sprites in a 3 x 2 grid, toggling every 4 frames between expanded ones
 * 48 px apart (D_A7B0 / D_A7B6) and plain ones 24 px apart (D_A7BC / D_A7C2), until the frame counter
 * (reset to 0..3) reaches 60 on a plain turn ($2AC4): about 64 frames.
 */
export const BOMB_BIG_X: readonly number[] = [0x54, 0x84, 0xb4, 0x54, 0x84, 0xb4];
export const BOMB_BIG_Y: readonly number[] = [0x67, 0x67, 0x67, 0x91, 0x91, 0x91];
export const BOMB_SMALL_X: readonly number[] = [0x78, 0x90, 0xa8, 0x78, 0x90, 0xa8];
export const BOMB_SMALL_Y: readonly number[] = [0x7c, 0x7c, 0x7c, 0x91, 0x91, 0x91];
export const BOMB_FRAMES = 64;
/** The plain grid's middle, X $78..$A8 + a sprite (for the renderer's blast centre). */
export const BOMB_X = 0x9c;
export const BOMB_TOGGLE_FRAMES = 4;

// --- The potion bonus stage (level-start.s D_2F7D..D_3080, platform-collision.s D_31A2) ---

/** Rows scanned: 2 x ((42 ^ levelIndex) reduced below 11 by tens) from map row 1 ($2F97, D_A428 $852A). */
export const BONUS_BAND_SEED = 0x2a;
/** It stops after the band in which the count reaches 85 ($303C). */
export const BONUS_MAX = 85;
/** 500 each: $50 added at the score's low byte (D_3193 with Y = $50). */
export const BONUS_UNITS = 50;
/** The stage's timer, 30 s ($306B), after 16 frames drawing the items (D_3169). */
export const BONUS_SECONDS = 30;
export const BONUS_DRAW_FRAMES = 16;
/** Then the results for 200 frames ($310A) and a normal level clear. */
export const BONUS_RESULT_FRAMES = 200;
/** PERFECT: 100,000 to a player who took at least as many as the other, else 50,000 (D_30A0). */
export const PERFECT_TOP_UNITS = 10_000;
export const PERFECT_OTHER_UNITS = 5_000;
/**
 * The stage's items are item graphics 63 (green potion: a note), 65 (purple: an arch), 64 (yellow: a
 * gem) - not foods ($A2C0 / $A300 / $A2E0, D_3169) - coloured $0D / $0C / $0F. `Item.item` must index
 * FOODS, so each carries the diamond of its colour as a stand-in; `effectsLook` names the real picture.
 */
export const BONUS_PICTURES: readonly { potion: number; picture: 'note' | 'arch' | 'gem'; colour: number; food: number }[] = [
  { potion: 16, picture: 'note', colour: 5, food: 43 },
  { potion: 17, picture: 'arch', colour: 4, food: 44 },
  { potion: 18, picture: 'gem', colour: 7, food: 41 },
];
